import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, api } from '../services/api.jsx';

const AuthContext = createContext(null);

const normalizePermissionName = (value = '') => String(value || '').trim().toLowerCase();
const splitRoles = (value = '') => String(value || '')
  .split(',')
  .map(role => role.trim().toLowerCase())
  .filter(Boolean);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  // Role override for superuser preview
  const [roleOverride, setRoleOverride] = useState(null);
  const [permissionOverride, setPermissionOverride] = useState(null);
  const [viewAsUser, setViewAsUser] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const isPublicRsvpRoute = window.location.pathname.startsWith('/events/rsvp/');
      if (isPublicRsvpRoute) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          // Fetch user photo after setting user
          await fetchUserPhoto(token);
        } catch (error) {
          console.error('Failed to load user profile', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const fetchUserPhoto = async (token) => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setUserPhoto(url);
      }
    } catch (error) {
      console.log('Could not fetch user photo:', error);
      // Silently fail and use fallback emoji
    }
  };

  const login = async () => {
    try {
      const response = await authAPI.login();
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Login failed', error);
      throw error;
    }
  };

  const handleCallback = async (code) => {
    try {
      const response = await authAPI.callback(code);
      const { accessToken, refreshToken } = response.data;
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      const profileResponse = await authAPI.getProfile();
      setUser(profileResponse.data);
      
      // Fetch user photo after setting user
      await fetchUserPhoto(accessToken);
      
      return true;
    } catch (error) {
      console.error('Callback failed', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setRoleOverride(null);
    setPermissionOverride(null);
    setViewAsUser(null);
    setUser(null);
  };

  const clearViewAsUser = () => {
    setRoleOverride(null);
    setPermissionOverride(null);
    setViewAsUser(null);
  };

  const userRoles = splitRoles(user?.role || '');

  const userPermissions = Array.isArray(user?.permissions)
    ? user.permissions.map(normalizePermissionName).filter(Boolean)
    : [];

  const isSuperuser = userRoles.includes('superuser');

  const baseHasPermission = (permissionName) => {
    const targetPermission = normalizePermissionName(permissionName);
    if (!targetPermission) return false;
    if (isSuperuser) return true;
    return userPermissions.includes(targetPermission);
  };

  const baseHasAnyPermission = (permissionNames = []) => {
    if (!Array.isArray(permissionNames) || permissionNames.length === 0) {
      return false;
    }

    return permissionNames.some(permissionName => baseHasPermission(permissionName));
  };

  const effectivePermissions = Array.isArray(permissionOverride)
    ? permissionOverride
    : userPermissions;

  const hasPermission = (permissionName) => {
    const targetPermission = normalizePermissionName(permissionName);
    if (!targetPermission) return false;
    if (isSuperuser && !Array.isArray(permissionOverride)) return true;
    return effectivePermissions.includes(targetPermission);
  };

  const hasAnyPermission = (permissionNames = []) => {
    if (!Array.isArray(permissionNames) || permissionNames.length === 0) {
      return false;
    }

    return permissionNames.some(permissionName => hasPermission(permissionName));
  };

  const canViewAsUsers = isSuperuser || baseHasAnyPermission(['admin:users:read', 'admin:users:write']) || userRoles.includes('admin');

  // Resolves permissions for a hypothetical role combination via the same v2/legacy-fallback logic the backend enforces.
  const resolvePermissionsForRoles = async (roleValue) => {
    const roleNames = splitRoles(roleValue);
    if (roleNames.length === 0) {
      return [];
    }

    try {
      const response = await api.post('/admin/permissions/resolve', { roles: roleNames });
      return Array.isArray(response.data?.permissions) ? response.data.permissions.map(normalizePermissionName) : [];
    } catch (error) {
      console.error('Failed to resolve permissions for role preview', error);
      return [];
    }
  };

  const applyRoleOverride = async (selectedRoleValue) => {
    const normalizedRoleValue = String(selectedRoleValue || '').trim();

    if (!normalizedRoleValue) {
      setRoleOverride(null);
      setPermissionOverride(null);
      return;
    }

    const previewPermissions = await resolvePermissionsForRoles(normalizedRoleValue);
    setRoleOverride(normalizedRoleValue);
    setPermissionOverride(previewPermissions);
  };

  const applyViewAsUser = async (selectedUser) => {
    if (!canViewAsUsers) {
      return;
    }

    if (!selectedUser) {
      clearViewAsUser();
      return;
    }

    const targetUserId = selectedUser.UserId || selectedUser.userId || '';
    let previewPermissions = [];
    let previewRoles = [];
    let previewSource = null;

    // Fetch the same authoritative context the backend middleware computes for this exact user,
    // instead of re-deriving permissions client-side from the row's Role string.
    try {
      const response = await api.get(`/admin/users/${targetUserId}/effective-authorization`);
      previewPermissions = Array.isArray(response.data?.permissions) ? response.data.permissions.map(normalizePermissionName) : [];
      previewRoles = Array.isArray(response.data?.roles) ? response.data.roles : [];
      previewSource = response.data?.source || null;
    } catch (error) {
      console.error('Failed to load effective authorization for view-as preview', error);
    }

    const selectedRole = previewRoles.join(',') || String(selectedUser.Role || selectedUser.role || '').trim() || 'user';
    setRoleOverride(selectedRole);
    setPermissionOverride(previewPermissions);
    setViewAsUser({
      userId: targetUserId,
      email: selectedUser.Email || selectedUser.email || '',
      displayName: selectedUser.DisplayName || selectedUser.displayName || selectedUser.Email || selectedUser.email || '',
      role: selectedRole,
      source: previewSource
    });
  };


  // Compute effective role (override if set)
  const effectiveRole = roleOverride || user?.role;

  const setRoleOverrideWithPermissions = async (nextRoleValue) => {
    if (!nextRoleValue) {
      setRoleOverride(null);
      setPermissionOverride(null);
      setViewAsUser(null);
      return;
    }

    setViewAsUser(null);
    await applyRoleOverride(nextRoleValue);
  };

  const value = {
    user,
    userPhoto,
    loading,
    login,
    logout,
    handleCallback,
    isAuthenticated: !!user,
    effectiveRole,
    roleOverride,
    setRoleOverride: setRoleOverrideWithPermissions,
    viewAsUser,
    applyViewAsUser,
    clearViewAsUser,
    canViewAsUsers,
    isViewingAsUser: canViewAsUsers && !!viewAsUser,
    permissions: effectivePermissions,
    hasPermission,
    hasAnyPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
